import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { useFormik } from "formik";

// Components
import {
  StatusPill,
  Button,
  Map,
  CentralModal,
  NotificationsCounter
} from "../UI";
import FooterActions from "./FooterActions";
import WorkerItem from "../lists/workers/Worker.item";
import ServiceComment from "../lists/serviceComments/ServiceComment.item";
import { useSideModalClose } from "../UI/Modals/SideModal";

// Builders
import * as buttonDesignType from "../../builders/buttonDesign.types";

// Constants
import * as statusTypes from "../../constants/status.constants";

// Containers
import BookedForForm from "../../containers/forms/bookedFor/booked-for.form";
import EtaForm from "../../containers/forms/eta/Eta.form";

// Icons
import { Close } from "../../SVGS";

// Actions
import {
  approveServiceRequests,
  openDeclineReason,
  setTiketDetailsModalType,
  updateServiceRequest
} from "../../redux/actions/service-requests.actions";
import {
  cancelService,
  startService,
  pauseService,
  resumeService,
  finishService,
  openWorkersForm,
  setServicesTiketDetailsModalType,
  updateService
} from "../../redux/actions/services.actions";
import {
  getServiceComments,
  deleteComment,
  createServiceComment
} from "../../redux/actions/service-comments.actions";

// Utils
import { isEmpty, enableScroll } from "../../utils";
import {
  convertTimeStampToRequestFormat,
  convertTimeStampToTime,
  convertTimeStampToDateWithSlash,
  convertWallClockTimeStampToDateWithSlash,
  convertTimeStampToRequestDate,
  convertTimeStampToUsDateTime
} from "../../utils/timeStampToDate";

// Styles
import { TiketDetailsStyles } from "./tiketDetails.styles";
import TextFieldWithButton from "../UI/TextField/TextFieldWithButton";
import { fetchBranchRequest } from "../../api/calls/branch.call";
import { BranchFormModel } from "../../models/app/forms";

const BOOKED_FOR_FORM = "BOOKED_FOR_FORM";
const ETA_FORM = "ETA_FORM";

const TiketDetails = ({
  closeModal,
  toggleChat,
  serviceDetails,
  tiketDetailsModalType,
  shouldShowWokers = false,
  isServicesDrawer
}) => {
  const { user } = useSelector(state => state.app);
  const location = useLocation();
  const dispatch = useDispatch();
  // Which redux slice owns this drawer (services vs service-requests).
  // Hosts that render from the services slice must pass isServicesDrawer;
  // the pathname fallback keeps legacy local hosts (Dashboard) unchanged.
  const isServicesContext =
    isServicesDrawer ??
    (location.pathname === "/services" ||
      location.pathname === "/employee-schedule");
  const sideModalClose = useSideModalClose();
  const handleClose = sideModalClose || closeModal;
  const { systemComponents } = useSelector(state => state.app);
  const { comments: serviceComments } = useSelector(
    state => state.serviceCommentsReducer
  );
  const [branch, setBranch] = useState();
  const isExternalService = serviceDetails.status === "external";

  const serviceType = systemComponents.serviceTypes.find(
    type => type.typeKey === serviceDetails?.serviceTypeKey
  );

  const isBookedActionVisible =
    serviceDetails?.status === statusTypes.PENDING ||
    serviceDetails?.status === undefined;

  const isVisible =
    serviceDetails?.status === statusTypes.IN_PROGRESS ||
    serviceDetails?.status === statusTypes.PAUSED ||
    serviceDetails?.status === statusTypes.ACCEPTED ||
    serviceDetails?.status === statusTypes.PENDING ||
    serviceDetails?.status === undefined;

  const shouldShowComments = ![statusTypes.EXTERNAL].includes(
    serviceDetails?.status
  );

  const approveAction = () => {
    dispatch(approveServiceRequests(serviceDetails.id));
  };

  const declineAction = () => {
    dispatch(openDeclineReason(serviceDetails.id));
  };

  const changeBookedForHandler = dateObject => {
    const timeStamp = Date.parse(dateObject) / 1000;
    const formatedDate = convertTimeStampToRequestDate(timeStamp);
    const formatedTime = convertTimeStampToRequestFormat(timeStamp);

    if (isEmpty(formatedDate) && isEmpty(formatedTime)) {
      return;
    }
    const payload = {
      time: `${formatedDate} ${formatedTime}`
    };

    dispatch(updateServiceRequest(serviceDetails.id, payload));
  };

  const changeETAHandler = dateObject => {
    const timeStamp = Date.parse(dateObject) / 1000;
    const formatedDate = convertTimeStampToRequestDate(timeStamp);
    const formatedTime = convertTimeStampToRequestFormat(timeStamp);
    if (isEmpty(formatedDate) && isEmpty(formatedTime)) {
      return;
    }
    const payload = {
      eta: `${formatedDate} ${formatedTime}`
    };

    if (isServicesContext) {
      dispatch(updateService(payload, serviceDetails.id));
      return;
    }

    dispatch(updateServiceRequest(serviceDetails.id, payload));
  };

  const cancelServiceAction = () => {
    enableScroll();
    dispatch(cancelService(serviceDetails.id));
  };

  const startServiceAction = () => {
    enableScroll();
    dispatch(startService(serviceDetails.id));
  };

  const pauseServiceAction = () => {
    enableScroll();
    dispatch(pauseService(serviceDetails.id));
  };

  const resumeServiceAction = () => {
    enableScroll();
    dispatch(resumeService(serviceDetails.id));
  };

  const finishServiceAction = () => {
    enableScroll();
    dispatch(finishService(serviceDetails.id));
  };

  useEffect(() => {
    dispatch(
      getServiceComments({
        serviceId: serviceDetails.id
      })
    );
  }, [dispatch, serviceDetails.id]);

  const fetchBranch = useCallback(async () => {
    if (!serviceDetails.branchId) return;
    const response = await fetchBranchRequest(serviceDetails.branchId);
    if (response.hasError) return;
    setBranch(new BranchFormModel(response.branch.generateBranchForm()));
  }, [serviceDetails.branchId]);

  useEffect(() => {
    fetchBranch();
  }, [fetchBranch]);

  const removeComment = serviceComment => {
    if (user.id !== serviceComment.user.id) {
      toast.error(`You can't delete other users comments.`);
      return;
    }

    dispatch(deleteComment(serviceComment.id));
  };

  const createServiceCommentHandler = () => {
    dispatch(
      createServiceComment(serviceDetails.id, {
        comment: formik.values.comment
      })
    );
    formik.values.comment = "";
  };

  const formik = useFormik({
    initialValues: { comment: "" }
  });

  const { values, errors, touched, handleBlur, handleChange } = formik;

  const openBookedForForm = () =>
    dispatch(
      setTiketDetailsModalType(BOOKED_FOR_FORM)
    );

  const openEtaForm = () => {
    if (isServicesContext) {
      dispatch(
        setServicesTiketDetailsModalType(
          ETA_FORM
        )
      );
      return;
    }
    dispatch(
      setTiketDetailsModalType(ETA_FORM)
    );
  };

  return (
    <>
      {serviceDetails && (
        <TiketDetailsStyles>
          <div className="tiketDetailsCard">
            <div className="sd-header">
              <h2 className="sd-header__title">
                {serviceDetails?.status === statusTypes.PENDING ||
                serviceDetails?.status === statusTypes.DECLINED ||
                serviceDetails?.status === undefined
                  ? "Service request"
                  : "Service details"}
                {serviceDetails?.id && (
                  <span className="sd-header__id">#{serviceDetails.id}</span>
                )}
              </h2>
              <div
                className="sd-header__close"
                onClick={handleClose}
                role="button"
                aria-label="Close"
              >
                <Close />
              </div>
            </div>

            <div className="sd-body">
              {branch && (
                <div className="sd-block">
                  <div className="sd-branch-info">
                    <div className="sd-branch-info__name">{branch.name}</div>
                    <div className="sd-branch-info__address">
                      {branch.address}
                    </div>
                  </div>
                </div>
              )}

              {!isEmpty(serviceDetails.location) && (
                <div className="sd-block">
                  <div className="sd-field">
                    <span className="label">Address</span>
                    <span className="value">
                      {serviceDetails.location.address}
                    </span>
                    <span className="meta">
                      {serviceDetails.location.state},{" "}
                      {serviceDetails.location.city}
                    </span>
                  </div>
                  <div className="sd-map-card">
                    <Button
                      className="copyLocation"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          serviceDetails.location.address
                        );
                        toast.success("Location Copied.");
                      }}
                    >
                      Copy Location
                    </Button>
                    <Map
                      center={{
                        lat: serviceDetails.location.latitude,
                        lng: serviceDetails.location.longitude
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="sd-block">
                <div className="sd-grid-2">
                  <div className="sd-field">
                    <span className="label">Driver</span>
                    <span className="value">
                      {isExternalService
                        ? "External user"
                        : `${serviceDetails?.driver?.firstName ?? ""} ${
                            serviceDetails?.driver?.lastName ?? ""
                          }`.trim()}
                    </span>
                    {serviceDetails?.phoneNumber && (
                      <span className="meta">
                        {serviceDetails.phoneNumber}
                      </span>
                    )}
                  </div>
                  <div className="sd-field">
                    <span className="label">Status</span>
                    {serviceDetails.status && (
                      <StatusPill status={serviceDetails.status} />
                    )}
                  </div>
                </div>

                <div className="sd-grid-2">
                  <div className="sd-field">
                    <span className="label">Service type</span>
                    {!isEmpty(serviceType) ? (
                      <>
                        <span className="value">{serviceType.name}</span>
                        {serviceDetails.mobility && (
                          <span className="meta">
                            {serviceDetails.mobility === "mobile"
                              ? "Remote"
                              : "On-site"}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="meta">—</span>
                    )}
                  </div>
                  <div className="sd-field">
                    <span className="label">Booked for</span>
                    <span className="value value--nowrap">
                      {convertTimeStampToUsDateTime(serviceDetails.time)}
                    </span>
                    {isBookedActionVisible && (
                      <button
                        type="button"
                        className="sd-link-btn"
                        onClick={openBookedForForm}
                      >
                        Change time
                      </button>
                    )}
                  </div>
                </div>

                {!isEmpty(serviceDetails.truck) && (
                  <div className="sd-card">
                    <span className="card-label">Truck</span>
                    <span className="card-title">
                      {serviceDetails.truck.manufacturer.manufacturer}{" "}
                      {serviceDetails.truck.model}
                    </span>
                    {serviceDetails.truck.last4Vin && (
                      <span className="card-meta">
                        VIN · {serviceDetails.truck.last4Vin}
                      </span>
                    )}
                  </div>
                )}

                {!isEmpty(serviceDetails.trailer) && (
                  <div className="sd-card">
                    <span className="card-label">Trailer</span>
                    <span className="card-title">
                      {serviceDetails.trailer.manufacturer.manufacturer}{" "}
                      {serviceDetails.trailer.type.name}
                    </span>
                    <span className="card-meta">
                      Year {serviceDetails.trailer.productionYear} · VIN{" "}
                      {serviceDetails.trailer.last4Vin}
                    </span>
                  </div>
                )}

                {isExternalService &&
                  serviceDetails.system_service_description && (
                    <div className="sd-card">
                      <span className="card-label">Description</span>
                      <span className="card-meta">
                        {serviceDetails.system_service_description}
                      </span>
                    </div>
                  )}

                {!isEmpty(serviceDetails.specialRequest) && (
                  <div className="sd-card">
                    <span className="card-label">Special request</span>
                    <span className="card-meta">
                      {serviceDetails.specialRequest}
                    </span>
                  </div>
                )}
              </div>

              {!(
                serviceDetails.mobility === "shop" &&
                serviceDetails?.status === statusTypes.PENDING
              ) && (
                <div className="sd-block">
                  <div className="sd-field">
                    <span className="label">
                      {serviceDetails.mobility === "mobile"
                        ? "ETA"
                        : "Est. ready"}
                    </span>
                    <div className="sd-row">
                      <div className="sd-row__values">
                        <span className="value">
                          {convertTimeStampToTime(serviceDetails.eta)}
                        </span>
                        <span className="meta">
                          {convertWallClockTimeStampToDateWithSlash(
                            serviceDetails.eta
                          )}
                        </span>
                      </div>
                      {isVisible && (
                        <Button
                          className="sd-estimate-btn sd-inline-btn"
                          buttonDesignType={buttonDesignType.SECONDARY}
                          onClick={openEtaForm}
                        >
                          Estimate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {shouldShowWokers && (
                <div className="sd-block">
                  <div className="sd-section-header">
                    <span className="section-title">Workers</span>
                    {isVisible && (
                      <Button
                        className="sd-inline-btn"
                        buttonDesignType={buttonDesignType.SECONDARY}
                        onClick={() =>
                          dispatch(
                            openWorkersForm(
                              serviceDetails.id,
                              serviceDetails.workers
                            )
                          )
                        }
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                  {serviceDetails.workers &&
                  serviceDetails.workers.length > 0 ? (
                    <div className="sd-workers-list">
                      {serviceDetails.workers.map(worker => (
                        <WorkerItem {...worker} key={worker.id} />
                      ))}
                    </div>
                  ) : (
                    <div className="sd-workers-empty">
                      No workers assigned
                    </div>
                  )}
                </div>
              )}

              {shouldShowComments && (
                <div className="sd-block">
                  <div className="sd-section-header">
                    <span className="section-title">Comments</span>
                  </div>
                  <div className="sd-comments-list">
                    {serviceComments.map(comment => (
                      <ServiceComment
                        serviceComment={comment}
                        key={comment.id}
                        deleteComment={() => removeComment(comment)}
                      />
                    ))}
                  </div>
                  <div className="sd-create-comment">
                    <TextFieldWithButton
                      id="comment"
                      name="comment"
                      type="text"
                      value={values.comment}
                      touched={touched.comment}
                      error={errors.comment}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onClick={createServiceCommentHandler}
                      disabled={values.comment === "" ? true : false}
                      maxLength={500}
                    />
                  </div>
                </div>
              )}
            </div>

            {isVisible && !isExternalService && (
              <div className="sd-messages-bar">
                <Button
                  className="messagesButton"
                  onClick={toggleChat}
                  buttonDesignType={buttonDesignType.SECONDARY}
                >
                  Messages
                  {serviceDetails.newMessagesCount > 0 && (
                    <NotificationsCounter
                      className="notificationCounter"
                      counter={serviceDetails.newMessagesCount}
                    />
                  )}
                </Button>
              </div>
            )}

            {!isExternalService && (
              <FooterActions
                status={serviceDetails.status}
                newMessagesCount={serviceDetails.newMessagesCount}
                closeModal={handleClose}
                toggleChat={toggleChat}
                approveAction={approveAction}
                declineAction={declineAction}
                cancelServiceAction={cancelServiceAction}
                startServiceAction={startServiceAction}
                pauseServiceAction={pauseServiceAction}
                resumeServiceAction={resumeServiceAction}
                finishServiceAction={finishServiceAction}
              />
            )}
          </div>
        </TiketDetailsStyles>
      )}

      {tiketDetailsModalType && (
        <CentralModal
          className="estimationCard"
          closeModal={() => {
            if (isServicesContext) {
              dispatch(setServicesTiketDetailsModalType(undefined));
              return;
            }
            dispatch(setTiketDetailsModalType(undefined));
          }}
        >
          {tiketDetailsModalType === ETA_FORM && (
            <EtaForm
              updateEstimationDate={changeETAHandler}
              initialDateValue={serviceDetails.eta}
              closeModal={() => {
                if (isServicesContext) {
                  dispatch(setServicesTiketDetailsModalType(undefined));
                  return;
                }
                dispatch(setTiketDetailsModalType(undefined));
              }}
            />
          )}
          {tiketDetailsModalType ===
            BOOKED_FOR_FORM && (
            <BookedForForm
              serviceId={serviceDetails.id}
              serviceTypeId={serviceDetails.serviceTypeId || serviceType?.id}
              updateBookigDate={changeBookedForHandler}
              initialDateValue={serviceDetails.time}
              closeModal={() => dispatch(setTiketDetailsModalType(undefined))}
            />
          )}
        </CentralModal>
      )}
    </>
  );
};

export default TiketDetails;
